import { useEffect, useMemo } from "react";
import { UseTabManagementProps } from "./types";
import { useTabsState } from "./use-tabs-state";
import { useTabApiSync } from "./use-tab-api-sync";
import { useTabDnD } from "./use-tab-dnd";
import { useTabDeletion } from "./use-tab-deletion";
import { useGetDashboardByIdQuery } from "@/store/api/dashboard-api";
import { generateTabCopyName } from "@/utils/dashboard-naming-utils";

export const useTabManagement = (props: UseTabManagementProps) => {
  // Subscribe to live dashboard data from RTK Query cache
  const { data: dashboardData, isFetching } = useGetDashboardByIdQuery(
    props.dashboardId || "",
    {
      skip: !props.dashboardId,
    },
  );

  // Convert dashboard tabs to TabData format - memoized to prevent infinite loops
  const liveTabs = useMemo(() => {
    if (!dashboardData?.tabs) return props.tabs;

    return [...dashboardData.tabs]
      .sort((a, b) => a.displayOrder - b.displayOrder)
      .map((tab) => ({
        id: tab.id,
        label: tab.name,
      }));
  }, [dashboardData?.tabs, props.tabs]);

  const { state, refs, actions } = useTabsState({ ...props, tabs: liveTabs });
  const api = useTabApiSync(state, actions, props);
  const dnd = useTabDnD(state, actions, api, props.onSave);
  const deletion = useTabDeletion(state, actions, api);

  // auto-open add mode
  useEffect(() => {
    if (props.shouldStartInAddMode) actions.startAdd();
  }, [props.shouldStartInAddMode, actions]);

  // focus when adding
  useEffect(() => {
    if (state.isAddingNewTab) refs.newTabInputRef.current?.focus();
  }, [state.isAddingNewTab, refs.newTabInputRef]);

  // public API — ergonomic and stable
  return {
    // State
    localTabs: state.localTabs,
    newTabName: state.newTabName,
    setNewTabName: actions.setNewTabName,
    newTabError: state.newTabError,
    tabErrors: state.tabErrors,
    draggedIndex: state.draggedIndex,
    dragOverIndex: state.dragOverIndex,
    isAddingNewTab: state.isAddingNewTab,
    hoveredTabId: state.hoveredTabId,
    hoveredNewItem: state.hoveredNewItem,
    deleteConfirmation: state.deleteConfirmation,
    isLoadingTabs: isFetching,

    // Refs
    newTabInputRef: refs.newTabInputRef,

    // Handlers
    handleUpdateTabName: actions.updateLabel,
    handleTabNameBlur: (id: string, label: string) => {
      const original = props.tabs.find((t) => t.id === id)?.label;
      actions.validateOnBlur(id, label, original);
    },
    handleAddTab: actions.startAdd,
    handleSaveNewTab: () => {
      const created = actions.addOptimistic(state.newTabName);
      if (!created) return; // validation failed already sets error
      props.onAddTab(created.label); // server creation; temporary tab remains until props syncs back
    },
    handleCancelNewTab: actions.cancelAdd,

    handleDeleteTabClick: deletion.handleDeleteClick,
    confirmDeleteTab: deletion.confirmDelete,
    cancelDeleteTab: deletion.cancelDelete,

    handleDuplicateTab: (tabId: string, currentName: string) => {
      const existingNames = state.localTabs.map((tab) => tab.label);
      const newName = generateTabCopyName(currentName, existingNames);
      props.onDuplicateTab?.(tabId, newName);
    },

    handleDragStart: dnd.handleDragStart,
    handleDragEnd: dnd.handleDragEnd,
    handleDragOver: dnd.handleDragOver,
    handleDragEnter: dnd.handleDragEnter,
    handleDragLeave: dnd.handleDragLeave,
    handleDrop: dnd.handleDrop,

    handleMouseEnter: (tabId: string) => actions.hoverTab(tabId),
    handleMouseLeave: () => actions.hoverTab(null),
    handleNewItemMouseEnter: () => actions.hoverNewItem(true),
    handleNewItemMouseLeave: () => actions.hoverNewItem(false),
  } as const;
};
